import { useCallback, useEffect, useRef } from "react";

/**
 * Splits a row's click gesture so single and double click are exclusive:
 * a single click runs `onSingle` after a short delay, and a double click
 * cancels that timer and runs only `onDouble`. Without this, double-clicking
 * an expand-on-click row toggles it open and shut before opening.
 *
 * `delayed: false` opts out for rows whose single action is idempotent and
 * cheap (select-only leaf rows) — there the extra run is invisible and the
 * instant feedback is worth more than exclusivity.
 *
 * The 250ms window is a compromise: long enough to catch most double-clicks
 * (OS thresholds run up to ~500ms), short enough that expansion still feels
 * immediate.
 *
 * Both callbacks receive the originating mouse event so selection handlers
 * can read shift/meta/ctrl modifiers.
 */
export function useSingleOrDoubleClick(
  onSingle: (e: React.MouseEvent) => void,
  onDouble: (e: React.MouseEvent) => void,
  options?: { delayed?: boolean; delayMs?: number },
) {
  const { delayed = true, delayMs = 250 } = options ?? {};
  const timerRef = useRef<number | null>(null);
  const lastEventRef = useRef<React.MouseEvent | null>(null);

  const cancelPending = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => cancelPending, [cancelPending]);

  const onClick = useCallback(
    (e: React.MouseEvent) => {
      // The second click of a double-click also fires `click` (detail 2) —
      // ignore it so only the dblclick handler acts.
      if (e.detail > 1) return;
      if (e.shiftKey) e.preventDefault();
      if (!delayed) {
        onSingle(e);
        return;
      }
      // Modifier clicks must select immediately — delaying would make
      // shift-range feel broken and risk the event going stale.
      if (e.shiftKey || e.metaKey || e.ctrlKey || e.altKey) {
        cancelPending();
        onSingle(e);
        return;
      }
      cancelPending();
      lastEventRef.current = e;
      timerRef.current = window.setTimeout(() => {
        timerRef.current = null;
        const ev = lastEventRef.current;
        lastEventRef.current = null;
        if (ev) onSingle(ev);
      }, delayMs);
    },
    [delayed, delayMs, onSingle, cancelPending],
  );

  const onDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      cancelPending();
      onDouble(e);
    },
    [onDouble, cancelPending],
  );

  return { onClick, onDoubleClick };
}
